import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { handleStructuredPaste } from '@/lib/pasteUtils';
import { toast } from 'sonner';
import { 
  Plus, 
  FileText,
  Download,
  DollarSign,
  Calendar,
  Briefcase,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Eye,
  Loader2,
  MoreVertical,
  Pencil
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import InvoiceViewer from '../components/InvoiceViewer';

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-slate-100 text-slate-800'
};

const STATUS_ICONS = {
  draft: FileText,
  sent: Send,
  paid: CheckCircle,
  overdue: Clock,
  cancelled: XCircle
};

const InvoicesPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [companySettings, setCompanySettings] = useState(null);
  
  // Invoice viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Edit invoice state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editForm, setEditForm] = useState({
    lineItems: [],
    taxRate: '0',
    notes: '',
    dueDate: '',
    dueDateType: 'upon_receipt'
  });
  const [saving, setSaving] = useState(false);
  
  const [invoiceForm, setInvoiceForm] = useState({
    hourlyRate: '75',
    includeExpenses: true,
    taxRate: '0',
    notes: '',
    dueDate: '',
    dueDateType: 'upon_receipt',
    selectedTimeEntries: [],
    selectedExpenses: []
  });

  const fetchData = async () => {
    try {
      const [invoicesRes, jobsRes, companyRes] = await Promise.all([
        axios.get(`${API}/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/company`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setInvoices(invoicesRes.data);
      setJobs(jobsRes.data);
      setCompanySettings(companyRes.data);
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleJobSelect = async (jobId) => {
    setSelectedJob(jobId);
    if (jobId) {
      try {
        const response = await axios.get(`${API}/jobs/${jobId}/invoice-data`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setJobData(response.data);
        // Auto-select all unbilled time entries and expenses
        setInvoiceForm(prev => ({
          ...prev,
          selectedTimeEntries: (response.data.unbilled_time_entries || response.data.time_entries || []).map(t => t.id),
          selectedExpenses: prev.includeExpenses 
            ? (response.data.unbilled_expense_items || response.data.expenses || []).map(e => e.id)
            : []
        }));
      } catch (err) {
        toast.error('Failed to load job data');
      }
    } else {
      setJobData(null);
      setInvoiceForm(prev => ({ ...prev, selectedTimeEntries: [], selectedExpenses: [] }));
    }
  };

  const calculateInvoice = () => {
    if (!jobData) return { lineItems: [], subtotal: 0, taxAmount: 0, total: 0 };
    
    const lineItems = [];
    const hourlyRate = parseFloat(invoiceForm.hourlyRate) || 0;
    
    // Get unbilled time entries (support both old and new API response format)
    const availableTimeEntries = jobData.unbilled_time_entries || jobData.time_entries || [];
    const availableExpenses = jobData.unbilled_expense_items || jobData.expenses || [];
    
    // Helper to format YYYY-MM-DD or ISO into "M/D/YYYY"
    const fmtDate = (s) => {
      if (!s) return '';
      const d = new Date(s.length > 10 ? s : s + 'T00:00:00');
      if (isNaN(d.getTime())) return s.slice(0, 10);
      return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
    };
    
    // Itemize each selected time entry as its own line so notes/descriptions appear on the invoice
    const selectedTimeEntries = availableTimeEntries
      .filter(t => invoiceForm.selectedTimeEntries.includes(t.id))
      .slice()
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    
    selectedTimeEntries.forEach((t) => {
      const datePrefix = t.date ? `${fmtDate(t.date)} — ` : '';
      const note = (t.description && t.description.trim()) ? t.description.trim() : `Professional Services — ${jobData.job.title}`;
      lineItems.push({
        description: `${datePrefix}${note}`,
        quantity: t.hours,
        unit_price: hourlyRate,
        amount: +(t.hours * hourlyRate).toFixed(2),
      });
    });
    
    // Itemize each selected expense as its own line with its category + notes
    if (invoiceForm.includeExpenses) {
      const selectedExpenseItems = availableExpenses
        .filter(e => invoiceForm.selectedExpenses.includes(e.id))
        .slice()
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      
      selectedExpenseItems.forEach((e) => {
        const datePrefix = e.date ? `${fmtDate(e.date)} — ` : '';
        const category = e.category ? e.category.charAt(0).toUpperCase() + e.category.slice(1) : 'Expense';
        const note = (e.description && e.description.trim()) ? e.description.trim() : '';
        const desc = note
          ? `${datePrefix}${category}: ${note}`
          : `${datePrefix}${category} (Reimbursable)`;
        lineItems.push({
          description: desc,
          quantity: 1,
          unit_price: e.amount,
          amount: e.amount,
        });
      });
    }
    
    const subtotal = +lineItems.reduce((sum, item) => sum + item.amount, 0).toFixed(2);
    const taxRate = parseFloat(invoiceForm.taxRate) || 0;
    const taxAmount = +(subtotal * (taxRate / 100)).toFixed(2);
    const total = +(subtotal + taxAmount).toFixed(2);
    
    return { lineItems, subtotal, taxAmount, total };
  };

  const handleCreateInvoice = async () => {
    if (!selectedJob || !jobData) {
      toast.error('Please select a job');
      return;
    }
    
    const { lineItems, subtotal, taxAmount, total } = calculateInvoice();
    
    if (lineItems.length === 0) {
      toast.error('No billable items selected for this invoice');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        job_id: selectedJob,
        customer_id: jobData.customer.id,
        line_items: lineItems,
        subtotal: subtotal,
        tax_rate: parseFloat(invoiceForm.taxRate) || 0,
        tax_amount: taxAmount,
        total: total,
        due_date: invoiceForm.dueDateType === 'upon_receipt' ? 'upon_receipt' : (invoiceForm.dueDate || null),
        notes: invoiceForm.notes || null,
        include_expenses: invoiceForm.includeExpenses,
        time_entry_ids: invoiceForm.selectedTimeEntries,
        expense_ids: invoiceForm.includeExpenses ? invoiceForm.selectedExpenses : []
      };
      
      const response = await axios.post(`${API}/invoices`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Invoice ${response.data.invoice_number} created!`);
      setDialogOpen(false);
      setSelectedJob(null);
      setJobData(null);
      setInvoiceForm({ 
        hourlyRate: '75', 
        includeExpenses: true, 
        taxRate: '0', 
        notes: '', 
        dueDate: '', 
        dueDateType: 'upon_receipt',
        selectedTimeEntries: [],
        selectedExpenses: []
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invoice marked as ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded');
    } catch (err) {
      toast.error('Failed to download invoice');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setViewerOpen(true);
  };

  const handleViewerStatusUpdate = async (invoiceId, newStatus) => {
    await handleUpdateStatus(invoiceId, newStatus);
    // Update the selected invoice in the viewer
    setSelectedInvoice(prev => prev ? {...prev, status: newStatus} : null);
  };

  const handleOpenEdit = (invoice) => {
    setEditingInvoice(invoice);
    // Parse the invoice date to YYYY-MM-DD format for the date input
    const invoiceDate = invoice.created_at ? invoice.created_at.split('T')[0] : '';
    setEditForm({
      lineItems: invoice.line_items || [],
      taxRate: (invoice.tax_rate || 0).toString(),
      notes: invoice.notes || '',
      dueDate: invoice.due_date === 'upon_receipt' ? '' : (invoice.due_date || ''),
      dueDateType: invoice.due_date === 'upon_receipt' ? 'upon_receipt' : 'custom',
      invoiceDate: invoiceDate
    });
    setEditDialogOpen(true);
  };

  const handleUpdateLineItem = (index, field, value) => {
    setEditForm(prev => {
      const newItems = [...prev.lineItems];
      newItems[index] = { ...newItems[index], [field]: value };
      // Recalculate amount if quantity or unit_price changed
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(newItems[index].quantity) || 0;
        const price = field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(newItems[index].unit_price) || 0;
        newItems[index].amount = qty * price;
      }
      return { ...prev, lineItems: newItems };
    });
  };

  const handleAddLineItem = () => {
    setEditForm(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', quantity: 1, unit_price: 0, amount: 0 }]
    }));
  };

  const handleRemoveLineItem = (index) => {
    setEditForm(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const calculateEditTotals = () => {
    const subtotal = editForm.lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const taxRate = parseFloat(editForm.taxRate) || 0;
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const handleSaveInvoice = async () => {
    if (!editingInvoice) return;
    
    const { subtotal, taxAmount, total } = calculateEditTotals();
    
    if (editForm.lineItems.length === 0) {
      toast.error('Invoice must have at least one line item');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        line_items: editForm.lineItems.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          amount: parseFloat(item.amount) || 0
        })),
        subtotal,
        tax_rate: parseFloat(editForm.taxRate) || 0,
        tax_amount: taxAmount,
        total,
        due_date: editForm.dueDateType === 'upon_receipt' ? 'upon_receipt' : (editForm.dueDate || null),
        notes: editForm.notes || null,
        invoice_date: editForm.invoiceDate ? `${editForm.invoiceDate}T00:00:00Z` : null
      };
      
      await axios.put(`${API}/invoices/${editingInvoice.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Invoice updated!');
      setEditDialogOpen(false);
      setEditingInvoice(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const filteredInvoices = invoices.filter(inv => 
    statusFilter === 'all' || inv.status === statusFilter
  );

  const totals = {
    draft: invoices.filter(i => i.status === 'draft').reduce((s, i) => s + i.total, 0),
    sent: invoices.filter(i => i.status === 'sent').reduce((s, i) => s + i.total, 0),
    paid: invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.total, 0)
  };

  const { lineItems, subtotal, taxAmount, total } = calculateInvoice();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Invoices</h1>
          <p className="text-slate-500 mt-1">Create and manage client invoices</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#1F3C4F] hover:bg-[#152a38]"
              data-testid="create-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden">
            <DialogHeader className="pb-2">
              <DialogTitle className="font-heading text-xl">Create Invoice</DialogTitle>
              <DialogDescription className="text-sm">
                Generate an invoice from job hours and expenses
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Left Column - Selection & Options */}
              <div className="lg:col-span-3 space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                {/* Job Selection */}
                <div className="space-y-1">
                  <Label className="text-sm">Select Job *</Label>
                  <Select value={selectedJob || ''} onValueChange={handleJobSelect}>
                    <SelectTrigger data-testid="invoice-job-select" className="h-9">
                      <SelectValue placeholder="Choose a job to invoice" />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.filter(j => j.status !== 'cancelled').map((job) => (
                        <SelectItem key={job.id} value={job.id}>
                          <span className="font-mono text-[#1F3C4F] mr-2">{job.job_number}</span>
                          {job.title} ({job.customer_name})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {jobData && (
                  <>
                    {/* Compact Job Summary */}
                    <div className="grid grid-cols-3 gap-2 p-3 bg-slate-50 rounded-lg">
                      <div className="text-center">
                        <p className="text-xs text-slate-500">Unbilled</p>
                        <p className="text-lg font-bold text-[#1F3C4F]">
                          {(jobData.unbilled_hours ?? jobData.total_hours ?? 0).toFixed(1)} hrs
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-500">Expenses</p>
                        <p className="text-lg font-bold text-[#FF1493]">
                          {formatCurrency(jobData.unbilled_expenses ?? jobData.total_expenses ?? 0)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-500">Already Billed</p>
                        <p className="text-lg font-semibold text-green-600">
                          {(jobData.billed_hours ?? 0).toFixed(1)} hrs
                        </p>
                      </div>
                    </div>

                    {/* Time Entries - Compact */}
                    {(jobData.unbilled_time_entries || jobData.time_entries || []).length > 0 && (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-semibold">Time Entries</Label>
                          <div className="flex gap-1">
                            <Button type="button" variant="ghost" size="sm" className="h-6 text-xs px-2"
                              onClick={() => setInvoiceForm(prev => ({
                                ...prev,
                                selectedTimeEntries: (jobData.unbilled_time_entries || jobData.time_entries || []).map(t => t.id)
                              }))}>
                              All
                            </Button>
                            <Button type="button" variant="ghost" size="sm" className="h-6 text-xs px-2"
                              onClick={() => setInvoiceForm(prev => ({ ...prev, selectedTimeEntries: [] }))}>
                              None
                            </Button>
                          </div>
                        </div>
                        <div className="max-h-28 overflow-y-auto border rounded divide-y text-sm">
                          {(jobData.unbilled_time_entries || jobData.time_entries || []).map((entry) => (
                            <label key={entry.id} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={invoiceForm.selectedTimeEntries.includes(entry.id)}
                                onChange={(e) => {
                                  setInvoiceForm(prev => ({
                                    ...prev,
                                    selectedTimeEntries: e.target.checked
                                      ? [...prev.selectedTimeEntries, entry.id]
                                      : prev.selectedTimeEntries.filter(id => id !== entry.id)
                                  }));
                                }}
                                className="w-3.5 h-3.5 text-[#1F3C4F] rounded"
                              />
                              <span className="font-medium">{entry.hours.toFixed(1)}h</span>
                              <span className="text-xs text-slate-400">{entry.date}</span>
                              {entry.description && (
                                <span className="text-xs text-slate-500 truncate flex-1">{entry.description}</span>
                              )}
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-slate-500">
                          {invoiceForm.selectedTimeEntries.length} selected ({(jobData.unbilled_time_entries || jobData.time_entries || [])
                            .filter(t => invoiceForm.selectedTimeEntries.includes(t.id))
                            .reduce((sum, t) => sum + t.hours, 0).toFixed(1)} hrs)
                        </p>
                      </div>
                    )}

                    {/* Settings Row - All inline */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Hourly Rate</Label>
                        <Input
                          type="number"
                          value={invoiceForm.hourlyRate}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, hourlyRate: e.target.value })}
                          className="h-8"
                          data-testid="hourly-rate-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Tax Rate (%)</Label>
                        <Input
                          type="number"
                          value={invoiceForm.taxRate}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, taxRate: e.target.value })}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Due Date</Label>
                        <Select
                          value={invoiceForm.dueDateType || 'upon_receipt'}
                          onValueChange={(value) => {
                            setInvoiceForm({ ...invoiceForm, dueDateType: value, dueDate: value === 'upon_receipt' ? '' : invoiceForm.dueDate });
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="upon_receipt">Upon Receipt</SelectItem>
                            <SelectItem value="custom">Custom Date</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {invoiceForm.dueDateType === 'custom' && (
                      <Input
                        type="date"
                        value={invoiceForm.dueDate}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, dueDate: e.target.value })}
                        className="h-8"
                      />
                    )}

                    {/* Expenses Toggle & Selection - Compact */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="includeExpenses"
                          checked={invoiceForm.includeExpenses}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setInvoiceForm(prev => ({ 
                              ...prev, 
                              includeExpenses: checked,
                              selectedExpenses: checked 
                                ? (jobData.unbilled_expense_items || jobData.expenses || []).map(exp => exp.id)
                                : []
                            }));
                          }}
                          className="w-3.5 h-3.5 text-[#1F3C4F] rounded"
                        />
                        <Label htmlFor="includeExpenses" className="text-sm cursor-pointer">
                          Include expenses ({formatCurrency(jobData.unbilled_expenses ?? jobData.total_expenses ?? 0)})
                        </Label>
                      </div>
                      
                      {invoiceForm.includeExpenses && (jobData.unbilled_expense_items || jobData.expenses || []).length > 0 && (
                        <div className="max-h-20 overflow-y-auto border rounded divide-y text-sm ml-5">
                          {(jobData.unbilled_expense_items || jobData.expenses || []).map((expense) => (
                            <label key={expense.id} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={invoiceForm.selectedExpenses.includes(expense.id)}
                                onChange={(e) => {
                                  setInvoiceForm(prev => ({
                                    ...prev,
                                    selectedExpenses: e.target.checked
                                      ? [...prev.selectedExpenses, expense.id]
                                      : prev.selectedExpenses.filter(id => id !== expense.id)
                                  }));
                                }}
                                className="w-3.5 h-3.5 text-[#FF1493] rounded"
                              />
                              <span className="capitalize text-xs">{expense.category}</span>
                              <span className="font-mono text-xs ml-auto">{formatCurrency(expense.amount)}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Notes - Compact */}
                    <div className="space-y-1">
                      <Label className="text-xs">Notes / Terms (optional)</Label>
                      <Textarea
                        placeholder="Payment terms, additional notes..."
                        value={invoiceForm.notes}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                        onPaste={handleStructuredPaste}
                        rows={2}
                        className="text-sm resize-none"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Right Column - Invoice Preview (Sticky) */}
              <div className="lg:col-span-2">
                <Card className="border-[#1F3C4F]/20 sticky top-0">
                  <CardHeader className="pb-2 pt-3">
                    <CardTitle className="text-sm font-heading flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Invoice Preview
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {!jobData ? (
                      <p className="text-sm text-slate-400 text-center py-8">Select a job to preview invoice</p>
                    ) : lineItems.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-8">No items selected</p>
                    ) : (
                      <div className="space-y-2">
                        {lineItems.map((item, idx) => (
                          <div key={idx} className="text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-600 truncate pr-2">{item.description}</span>
                            </div>
                            <div className="flex justify-between text-xs text-slate-500">
                              <span>{item.quantity} × {formatCurrency(item.unit_price)}</span>
                              <span className="font-mono">{formatCurrency(item.amount)}</span>
                            </div>
                          </div>
                        ))}
                        <div className="border-t pt-2 mt-3 space-y-1">
                          <div className="flex justify-between text-sm">
                            <span>Subtotal</span>
                            <span className="font-mono">{formatCurrency(subtotal)}</span>
                          </div>
                          {taxAmount > 0 && (
                            <div className="flex justify-between text-sm text-slate-500">
                              <span>Tax ({invoiceForm.taxRate}%)</span>
                              <span className="font-mono">{formatCurrency(taxAmount)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-bold text-lg pt-2 border-t">
                            <span>Total</span>
                            <span className="font-mono text-[#1F3C4F]">{formatCurrency(total)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>

            <DialogFooter className="pt-3 border-t mt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateInvoice}
                disabled={creating || !selectedJob || lineItems.length === 0}
                className="bg-[#1F3C4F] hover:bg-[#152a38]"
                data-testid="invoice-submit-btn"
              >
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create Invoice
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-[#1F3C4F] to-[#152a38]">
          <CardContent className="p-6">
            <div className="text-white">
              <p className="text-white/70 text-sm">Total Invoiced</p>
              <p className="text-3xl font-heading font-bold mt-2">
                {formatCurrency(invoices.reduce((s, i) => s + i.total, 0))}
              </p>
              <p className="text-white/70 text-sm mt-1">{invoices.length} invoices</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FileText className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Draft</p>
                <p className="text-xl font-heading font-bold">{formatCurrency(totals.draft)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Send className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Sent</p>
                <p className="text-xl font-heading font-bold">{formatCurrency(totals.sent)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Paid</p>
                <p className="text-xl font-heading font-bold">{formatCurrency(totals.paid)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoices List */}
      {filteredInvoices.length > 0 ? (
        <div className="space-y-3" data-testid="invoices-list">
          {filteredInvoices.map((invoice) => {
            const StatusIcon = STATUS_ICONS[invoice.status] || FileText;
            return (
              <Card 
                key={invoice.id} 
                className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer"
                data-testid={`invoice-${invoice.invoice_number}`}
                onClick={() => handleViewInvoice(invoice)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#1F3C4F]/10 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-[#1F3C4F]" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-mono text-lg font-semibold text-[#1F3C4F]">
                            {invoice.invoice_number}
                          </span>
                          <Badge className={STATUS_STYLES[invoice.status]}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {invoice.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600">
                          {invoice.customer_name} {invoice.customer_company && `(${invoice.customer_company})`}
                        </p>
                        <p className="text-xs text-slate-400">
                          Job: {invoice.job_number} • Created: {new Date(invoice.created_at).toLocaleDateString()}
                          {invoice.due_date && ` • Due: ${new Date(invoice.due_date).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-2xl font-heading font-bold text-[#1F3C4F]">
                          {formatCurrency(invoice.total)}
                        </p>
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewInvoice(invoice)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Invoice
                            </DropdownMenuItem>
                            {['draft', 'sent'].includes(invoice.status) && (
                              <DropdownMenuItem onClick={() => handleOpenEdit(invoice)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Edit Invoice
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDownloadPDF(invoice.id, invoice.invoice_number)}>
                              <Download className="w-4 h-4 mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                            {invoice.status === 'draft' && (
                              <>
                                <DropdownMenuItem onClick={() => handleUpdateStatus(invoice.id, 'sent')}>
                                  <Send className="w-4 h-4 mr-2" />
                                  Mark as Sent
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleUpdateStatus(invoice.id, 'paid')}>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Mark as Paid
                                </DropdownMenuItem>
                              </>
                          )}
                          {invoice.status === 'sent' && (
                            <DropdownMenuItem onClick={() => handleUpdateStatus(invoice.id, 'paid')}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Mark as Paid
                            </DropdownMenuItem>
                          )}
                          {['draft', 'sent'].includes(invoice.status) && (
                            <DropdownMenuItem onClick={() => handleUpdateStatus(invoice.id, 'cancelled')}>
                              <XCircle className="w-4 h-4 mr-2" />
                              Cancel
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => handleDelete(invoice.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="font-heading font-semibold text-lg text-slate-700">No invoices found</h3>
            <p className="text-slate-500 mt-1">Create your first invoice from a job</p>
          </CardContent>
        </Card>
      )}

      {/* Invoice Viewer Modal */}
      <InvoiceViewer
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        invoice={selectedInvoice}
        onDownloadPDF={handleDownloadPDF}
        onUpdateStatus={handleViewerStatusUpdate}
        companySettings={companySettings}
      />

      {/* Edit Invoice Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-heading text-xl">
              Edit Invoice {editingInvoice?.invoice_number}
            </DialogTitle>
            <DialogDescription>
              Modify line items, tax rate, due date, and notes
            </DialogDescription>
          </DialogHeader>

          {editingInvoice && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Left Column - Line Items & Options */}
              <div className="lg:col-span-3 space-y-4 max-h-[55vh] overflow-y-auto pr-2">
                {/* Invoice Info */}
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-slate-500">Customer:</span>
                      <span className="ml-2 font-medium">{editingInvoice.customer_name}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Job:</span>
                      <span className="ml-2 font-mono text-[#1F3C4F]">{editingInvoice.job_number}</span>
                    </div>
                  </div>
                </div>

                {/* Line Items */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Line Items</Label>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddLineItem}>
                      <Plus className="w-3 h-3 mr-1" /> Add Item
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {editForm.lineItems.map((item, index) => (
                      <div key={index} className="p-3 border rounded-lg space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <Textarea
                              placeholder="Description (paste multi-line text — line breaks will be preserved)"
                              value={item.description}
                              onChange={(e) => handleUpdateLineItem(index, 'description', e.target.value)}
                              onPaste={handleStructuredPaste}
                              rows={item.description && item.description.includes('\n') ? Math.min(10, item.description.split('\n').length + 1) : 2}
                              className="text-sm resize-y"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveLineItem(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs text-slate-500">Qty</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={item.quantity}
                              onChange={(e) => handleUpdateLineItem(index, 'quantity', e.target.value)}
                              className="h-8 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Unit Price</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => handleUpdateLineItem(index, 'unit_price', e.target.value)}
                              className="h-8 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Amount</Label>
                            <Input
                              type="number"
                              value={item.amount}
                              readOnly
                              className="h-8 text-sm bg-slate-50"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tax & Due Date */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Invoice Date</Label>
                    <Input
                      type="date"
                      value={editForm.invoiceDate}
                      onChange={(e) => setEditForm(prev => ({ ...prev, invoiceDate: e.target.value }))}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tax Rate (%)</Label>
                    <Input
                      type="number"
                      value={editForm.taxRate}
                      onChange={(e) => setEditForm(prev => ({ ...prev, taxRate: e.target.value }))}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Due Date</Label>
                    <Select
                      value={editForm.dueDateType}
                      onValueChange={(value) => setEditForm(prev => ({ 
                        ...prev, 
                        dueDateType: value, 
                        dueDate: value === 'upon_receipt' ? '' : prev.dueDate 
                      }))}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="upon_receipt">Upon Receipt</SelectItem>
                        <SelectItem value="custom">Custom Date</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {editForm.dueDateType === 'custom' && (
                  <Input
                    type="date"
                    value={editForm.dueDate}
                    onChange={(e) => setEditForm(prev => ({ ...prev, dueDate: e.target.value }))}
                    className="h-8"
                  />
                )}

                {/* Notes */}
                <div className="space-y-1">
                  <Label className="text-xs">Notes / Terms</Label>
                  <Textarea
                    placeholder="Payment terms, additional notes..."
                    value={editForm.notes}
                    onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                    onPaste={handleStructuredPaste}
                    rows={2}
                    className="text-sm resize-none"
                  />
                </div>
              </div>

              {/* Right Column - Totals Preview */}
              <div className="lg:col-span-2">
                <Card className="border-[#1F3C4F]/20 sticky top-0">
                  <CardHeader className="pb-2 pt-3">
                    <CardTitle className="text-sm font-heading flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Invoice Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {editForm.lineItems.map((item, idx) => (
                        <div key={idx} className="text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600 truncate pr-2">{item.description || 'Item ' + (idx + 1)}</span>
                          </div>
                          <div className="flex justify-between text-xs text-slate-500">
                            <span>{item.quantity} × {formatCurrency(item.unit_price)}</span>
                            <span className="font-mono">{formatCurrency(item.amount)}</span>
                          </div>
                        </div>
                      ))}
                      <div className="border-t pt-2 mt-3 space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>Subtotal</span>
                          <span className="font-mono">{formatCurrency(calculateEditTotals().subtotal)}</span>
                        </div>
                        {calculateEditTotals().taxAmount > 0 && (
                          <div className="flex justify-between text-sm text-slate-500">
                            <span>Tax ({editForm.taxRate}%)</span>
                            <span className="font-mono">{formatCurrency(calculateEditTotals().taxAmount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold text-lg pt-2 border-t">
                          <span>Total</span>
                          <span className="font-mono text-[#1F3C4F]">{formatCurrency(calculateEditTotals().total)}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          <DialogFooter className="pt-3 border-t mt-4">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveInvoice}
              disabled={saving || editForm.lineItems.length === 0}
              className="bg-[#1F3C4F] hover:bg-[#152a38]"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPage;
